import { forgotPasswordSchema } from '@nexo/types';
import { Button, Field, Input } from '@nexo/ui';
import { MailCheck } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage, validate } from '../../lib/forms';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { data, errors } = validate(forgotPasswordSchema, { email });
    if (errors) return setError(errors.email);
    setLoading(true);
    try {
      await api.auth.forgotPassword(data.email);
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={sent ? t('Revisa tu correo') : t('¿Olvidaste tu contraseña?')}
      subtitle={sent ? '' : t('Te enviaremos un enlace para crear una nueva.')}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          {t('Volver a iniciar sesión')}
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-border bg-surface p-4 text-sm text-muted">
          <MailCheck className="size-6 text-success" />
          <p>
            <Trans
              i18nKey="Si existe una cuenta con <b>{{email}}</b>, recibirás un email con un enlace para restablecer la contraseña. Caduca en 1 hora."
              values={{ email }}
              components={{ b: <strong className="text-text" /> }}
            />
          </p>
          <p className="text-xs text-subtle">{t('¿No llega? Revisa la carpeta de spam.')}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field label={t('Email')} error={error}>
            {(props) => (
              <Input
                {...props}
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(undefined);
                }}
              />
            )}
          </Field>
          <Button type="submit" size="lg" loading={loading} className="w-full">
            {t('Enviar enlace')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
